<template>
  <div v-if="isVisible" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div class="relative top-10 mx-auto p-5 border max-w-6xl shadow-lg rounded-md bg-white">
      <div class="mt-3">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-sm md:text-lg font-medium text-gray-900 font-semibold">
            Vista previa: {{ selectedDocument?.document || 'Documento de Normatividad' }}
          </h3>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Contenido principal -->
        <div v-if="selectedDocument" class="mb-4">
          
          <div v-if="loadingViewer" class="flex justify-center py-8 bg-gray-50 rounded">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
          
          <div v-else-if="viewerError" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {{ viewerError }}
            <div class="mt-2">
              <button
                @click="openInNewTab"
                class="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Abrir en nueva pestaña
              </button>
            </div>
          </div>
          
          <div v-else class="border rounded bg-white">
            <!-- Visor de documento optimizado -->
            <div class="relative">
              <!-- Barra de herramientas del visor -->
              <div class="p-2 bg-gray-100 border-b rounded-t flex justify-between items-center">
                <div class="text-sm text-gray-600 font-medium truncate flex-1 mr-4">
                  {{ selectedDocument.document }}
                </div>
                <div class="flex items-center space-x-2">
                  <button
                    @click="openInNewTab"
                    class="inline-flex items-center px-3 py-1 text-xs text-gray-600 hover:text-gray-800 bg-white hover:bg-gray-50 border border-gray-300 rounded"
                    title="Abrir en nueva pestaña"
                  >
                    <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                    </svg>
                    Nueva pestaña
                  </button>
                </div>
              </div>
              
              <!-- Contenedor del documento -->
              <div class="relative bg-white">
                <!-- Iframe para escritorio -->
                <iframe
                  v-if="!isMobile && !showMobileFallback"
                  :src="pdfViewerUrl"
                  class="w-full border-0 rounded-b"
                  :style="{ height: '70vh', minHeight: '500px' }"
                  frameborder="0"
                  @load="onIframeLoad"
                  @error="onIframeError"
                  allow="autoplay; encrypted-media"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                ></iframe>
                
                <!-- Vista para móviles -->
                <div
                  v-else-if="isMobile && !showMobileFallback"
                  class="w-full rounded-b bg-gray-50 flex items-center justify-center"
                  :style="{ height: '60vh', minHeight: '400px' }"
                >
                  <!-- Intentar mostrar con object primero -->
                  <object
                    :data="selectedDocument.medialink"
                    type="application/pdf"
                    class="w-full h-full"
                    @error="onEmbedError"
                  >
                    <!-- Si object falla, mostrar botones de acción -->
                    <div class="flex flex-col items-center justify-center h-full p-4">
                      <svg class="w-16 h-16 text-blue-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                      <p class="text-gray-600 text-center mb-4">Documento disponible</p>
                      <button
                        @click="openInNewTab"
                        class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Abrir documento
                      </button>
                    </div>
                  </object>
                </div>
                
                <!-- Fallback para cuando no funciona la vista previa -->
                <div v-if="showMobileFallback" class="bg-white flex flex-col items-center justify-center p-8" :style="{ height: isMobile ? '60vh' : '70vh', minHeight: isMobile ? '400px' : '500px' }">
                  <svg class="w-16 h-16 text-blue-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                  <p class="text-gray-700 text-center mb-2 font-medium">
                    Documento listo para visualizar
                  </p>
                  <p class="text-gray-500 text-center mb-4 text-sm px-4">
                    {{ selectedDocument.document }}
                  </p>
                  <div class="flex flex-col sm:flex-row gap-3 w-full max-w-md">
                    <button
                      @click="openInNewTab"
                      class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Abrir documento
                    </button>
                    <button
                      @click="downloadDocument"
                      class="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Descargar archivo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mensajes de estado -->
        <div v-if="successMessage" class="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {{ successMessage }}
        </div>

        <div v-if="errorMessage" class="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {{ errorMessage }}
        </div>

        <!-- Botón cerrar -->
        <div class="flex justify-end">
          <button
            @click="closeModal"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NormatividadModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    selectedDocument: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loadingViewer: false,
      viewerError: null,
      showMobileFallback: false,
      iframeLoaded: false,
      successMessage: '',
      errorMessage: ''
    };
  },
  computed: {
    isMobile() {
      return window.innerWidth < 768;
    },
    pdfViewerUrl() {
      if (!this.selectedDocument?.medialink) return '';
      
      // Para navegadores que soportan visor PDF nativo
      if (this.isMobile) {
        // En móviles, usar Google Docs viewer con parámetros
        return `https://docs.google.com/gview?url=${encodeURIComponent(this.selectedDocument.medialink)}&embedded=true#toolbar=1&navpanes=0&scrollbar=1&view=FitH`;
      } else {
        // En escritorio, usar Google Docs viewer
        return `https://docs.google.com/gview?url=${encodeURIComponent(this.selectedDocument.medialink)}&embedded=true`;
      }
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.resetViewer();
      }
    },
    selectedDocument() {
      this.resetViewer();
    }
  },
  methods: {
    resetViewer() {
      this.loadingViewer = false;
      this.viewerError = null;
      this.showMobileFallback = false;
      this.iframeLoaded = false;
      this.clearMessages();
    },

    onIframeLoad() {
      this.iframeLoaded = true;
    },
    
    onIframeError() {
      console.log('Error cargando iframe, intentando con Google Docs viewer');
      this.tryGoogleDocsViewer();
    },
    
    tryGoogleDocsViewer() {
      if (this.selectedDocument?.medialink && !this.isMobile) {
        const iframe = this.$el.querySelector('iframe');
        if (iframe) {
          const googleDocsUrl = `https://docs.google.com/gview?url=${encodeURIComponent(this.selectedDocument.medialink)}&embedded=true`;
          iframe.src = googleDocsUrl;
          
          // Si Google Docs también falla, mostrar fallback
          iframe.onerror = () => {
            console.log('Google Docs viewer también falló');
            this.showMobileFallback = true;
          };
        }
      } else {
        this.showMobileFallback = true;
      }
    },
    
    onEmbedError() {
      console.log('Error cargando object, mostrando fallback');
      this.showMobileFallback = true;
    },

    openInNewTab() {
      if (this.selectedDocument?.medialink) {
        window.open(`https://docs.google.com/gview?url=${encodeURIComponent(this.selectedDocument.medialink)}&embedded=true`, '_blank', 'noopener,noreferrer');
      }
    },

    downloadDocument() {
      if (!this.selectedDocument?.medialink) return;
      
      this.clearMessages();
      
      try {
        // Crear un enlace temporal para descargar usando la URL original
        const link = document.createElement('a');
        link.href = this.selectedDocument.medialink;
        link.download = this.selectedDocument.document;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        // Añadir al DOM temporalmente
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.successMessage = `Descarga de "${this.selectedDocument.document}" iniciada.`;
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);
      } catch (error) {
        console.error('Error iniciando descarga:', error);
        this.errorMessage = 'Error al iniciar la descarga.';
      }
    },

    closeModal() {
      this.resetViewer();
      this.$emit('close');
    },

    clearMessages() {
      this.successMessage = '';
      this.errorMessage = '';
    }
  }
};
</script>